<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Vendas | Relatório de Recebimentos" :on-report="() => datatable?.openDialogReport()" />
    <t-floating-bar v-if="eventTitle">
      {{ eventTitle }}
    </t-floating-bar>
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :report="report"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :pagination="pagination"
          :metadata="metadata"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split("-")[0] }}</span>
          </template>
          <template #[`item.status`]="{ item }">
            <span
              :style="{
                backgroundColor: $formatters.payment(item.status).color,
              }"
              class="status-indicator"
              :title="$formatters.payment(item.status).text"
            />
          </template>
          <template #[`item.total_value`]="{ item }">
            <span v-if="can('read', 'fees')">
              {{ $formatters.currency(item.order.total_value ?? 0) }}
            </span>
            <span v-else>
              {{ $formatters.currency((item.order.total_value ?? 0) / ((item.order.event?.convenience_fee ?? 0) / 100 + 1)) }}
            </span>
          </template>
          <template #[`item.amount`]="{ item }">
            <span v-if="can('read', 'fees')">
              {{ $formatters.currency(item.amount) }}
            </span>
            <span v-else>
              {{ $formatters.currency(item.amount / ((item.order.event?.convenience_fee ?? 0) / 100 + 1)) }}
            </span>
          </template>

          <template #[`item.installment_number`]="{ item }">
            <span> {{ item.installment_number }}/{{ item.max_installments }} </span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, type Ref, useTemplateRef } from 'vue'
import type { AxiosResponse } from 'axios'
import TPageTitle from '@/components/TPageTitle.vue'
import TFloatingBar from '@/components/TFloatingBar.vue'
import type { DataTableFilter, DataTableHeader, DataTableMeta, DataTableMetaItem, DataTablePagination } from '@/types/DataTable'
import type { Report, ReportItem } from '@/types/Report'
import type PaginatedResponse from '@/types/PaginatedResponse'
import type { Invoice } from '@/types/Invoice'
import DataTable, { type DataTableType } from '@/components/datatable/DataTable.vue'
import InvoiceService from '@/services/InvoiceService'
import ProducerService from '@/services/ProducerService'
import { PaymentMethodLabels, PaymentMethods } from '@/constants/PaymentMethods'
import { eventName } from '@/plugins/eventName'
import { useSelectFilter } from '@/helpers/reports/selectFilter'
import { useDateIntervalFilter } from '@/helpers/reports/dateIntervalFilter'
import { useAbility } from '@casl/vue'
import { useSnackBar } from '@/stores/snackbar'
import { currency } from '@/helpers/formatters/currency'

const { can } = useAbility()
const snackbar = useSnackBar()

const datatable = useTemplateRef<DataTableType>('datatable')

const headers: DataTableHeader[] = [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  {
    title: 'Status',
    value: 'status',
    key: 'status',
    sortable: false,
    align: 'center',
  },
  {
    title: 'Número da Compra',
    value: 'order.order_number',
    key: 'order_number',
    sortable: false,
    align: 'center',
  },
  {
    title: 'Método de Pagamento',
    value: 'order.payment_method.name',
    key: 'payment_method',
    sortable: false,
  },
  {
    title: 'Pagador',
    value: 'order.owner.name',
    key: 'owner_name',
    sortable: false,
  },
  {
    title: 'Evento',
    value: 'order.event_name',
    key: 'event',
    sortable: false,
  },
  {
    title: 'Parcela',
    value: 'installment_number',
    key: 'installment_number',
    sortable: false,
    align: 'center',
  },
  {
    title: 'Data da Pagamento',
    value: 'paid_at',
    key: 'paid_at',
    sortable: false,
    align: 'center',
  },
  {
    title: 'Data de Recebimento',
    value: 'received_at',
    key: 'received_at',
    sortable: false,
    align: 'center',
  },
  {
    title: 'Valor Parcela',
    value: 'amount',
    key: 'amount',
    sortable: false,
  },
  {
    title: 'Valor Compra',
    value: 'order.total_value',
    key: 'total_value',
    sortable: false,
  },
]

const pagination: DataTablePagination = {
  page: 1,
  itemsPerPage: 50,
  total: 0,
  totalPages: 0,
}

const report: Ref<Report> = ref({
  items: [
    { label: 'ID', field: 'id', active: true, mandatory: false },
    { label: 'Status', field: 'status', active: true, mandatory: false },
    {
      label: 'Número da compra',
      field: 'order.order_number',
      active: true,
    },
    {
      label: 'Forma de pagamento',
      field: 'order.payment_method.name',
      active: true,
    },
    {
      label: 'Nome do comprador',
      field: 'order.owner.name',
      active: true,
    },
    { label: 'Evento', field: 'order.event_name', active: true },
    { label: 'Data da Compra', field: 'order.created_at', active: true },
    { label: 'Data de Vencimento', field: 'due_at', active: true },
    { label: 'Data de Pagamento', field: 'paid_at', active: true },
    { label: 'Data de Recebimento', field: 'received_at', active: true },
    { label: 'Número da Parcela', field: 'installment_number', active: true },
    { label: 'Total de Parcelas', field: 'max_installments', active: true },
    { label: 'Valor Parcela', field: 'amount', active: true },
    { label: 'Valor Total', field: 'order.total_value', active: true },
  ] as ReportItem[],
  filename: 'relatorio_faturamento',
  provider: async (filters: string, selected) => {
    return await new InvoiceService().report(filters, selected)
  },
})

// event_id keeps a "Todos" option and is never auto-selected, matching this
// file's original filterProvider (concatenated onto the initial "Todos"
// item and never set filters.event_id.value). Constructed directly since
// useEventFilter() would auto-select the first event, which this file never
// did.
const eventItems: Ref<{ title: string, value: string }[]> = ref([ { title: 'Todos', value: '' } ])
const eventModel: Ref<string | null> = ref('')
const eventReady = Promise.withResolvers<boolean>()

const eventFilter: DataTableFilter<string> = {
  model: eventModel,
  value: computed(() => eventModel.value),
  items: computed(() => eventItems.value),
  label: 'Evento',
  field: 'event_id',
  type: 'autocomplete',
  loading: ref(false),
  ready: eventReady.promise,
  required: false,
}

async function loadEventOptions() {
  const events = (await new ProducerService().events(1, '', 50)).data.data

  if (events.length > 0) {
    eventItems.value = eventItems.value.concat(events.map(e => ({ title: eventName(e), value: e.id })))
  }

  eventReady.resolve(true)
}

loadEventOptions()

const filters: Ref<DataTableFilter[]> = ref([
  eventFilter,
  // Deliberately only these 3 methods (not the full usePaymentMethodFilter()
  // list) - matches the original's own hardcoded subset.
  useSelectFilter({
    label: 'Método de Pagamento',
    field: 'payment_method',
    icon: 'fa-regular fa-money-bill',
    items: [
      { title: 'Todos', value: '' },
      { title: PaymentMethodLabels[PaymentMethods.CREDIT_CARD], value: PaymentMethods.CREDIT_CARD.toString() },
      { title: PaymentMethodLabels[PaymentMethods.BANKSLIP], value: PaymentMethods.BANKSLIP.toString() },
      { title: PaymentMethodLabels[PaymentMethods.PIX], value: PaymentMethods.PIX.toString() },
    ],
    required: false,
    initial: '',
  }),
  useDateIntervalFilter({
    label: 'Data de Recebimento',
    field: 'received_at',
    icon: 'fa-regular fa-calendar-days',
    required: false,
    initial: null,
  }),
])

const metadata: Ref<DataTableMeta> = ref({
  headers: [
    {
      title: 'Quantidade de parcelas',
      value: 'count',
      key: 'count',
      align: 'center',
      sortable: false,
    },
    {
      title: 'Valor total',
      value: 'value',
      key: 'value',
      align: 'center',
      sortable: false,
    },
  ],
  items: [] as DataTableMetaItem[],
})

const eventTitle = computed(() => {
  const found = eventItems.value.find(i => i.value === eventModel.value)
  return found?.value ? found.title : null
})

async function fetch(page: number, filtersString: string, per_page?: number): Promise<AxiosResponse<PaginatedResponse<Invoice>>> {
  try {
    const response = await new InvoiceService().index(page, filtersString, per_page) as unknown as AxiosResponse<PaginatedResponse<Invoice> & { additional: { value: number, fees: number, count: number } }>

    let revenue = response.data.additional.value

    if (!can('read', 'fees')) {
      revenue -= response.data.additional.fees
    }

    metadata.value.items = [
      {
        count: response.data.additional.count,
        value: currency(revenue / 100),
      },
    ]

    return response
  } catch (error) {
    snackbar.show('Não foi possível buscar os dados de faturamento.', 'error')

    throw error
  }
}
</script>

<style>
.status-indicator {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 5px;
}
</style>
